import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import { Lead } from "@mapmycustomers/shared/types/entity";
import { Button } from "@mapmycustomers/ui";

import Interactive from "@app/component/map/FeaturedMap/essentials/controls/Interactive";
import OverlayView from "@app/component/map/FeaturedMap/essentials/OverlayView";
import useMap from "@app/component/map/FeaturedMap/utils/useMap";
import useMapView from "@app/component/view/utils/useMapView";
import {
  cleanLassoPaths,
  selectMapTool,
  selectMultiplyItems,
  setHighlight,
} from "@app/scene/map/store/actions";
import { getMapLassoPaths, getMapLassoPathsCount } from "@app/scene/map/store/selectors";
import { RootState } from "@app/store/rootReducer";
import GeoPath from "@app/types/GeoPath";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { leadIdGetter } from "@app/util/map/idGetters";

import styles from "./LassoAnnotation.module.scss";

interface Props {
  cleanLassoPaths: typeof cleanLassoPaths;
  leads: Array<Lead>;
  paths: Array<GeoPath>;
  pathsCount: number;
  selectMapTool: typeof selectMapTool;
  selectMultiplyItems: typeof selectMultiplyItems;
  setHighlight: typeof setHighlight;
}

const messages = defineMessages({
  count: {
    id: "map.tool.lasso.leadAnnotation.count",
    defaultMessage: "{count} {count, plural, one {lead} other {leads}} lassoed",
    description: "Number of leads lassoed",
  },
  emptySelection: {
    id: "map.tool.lasso.leadAnnotation.emptySelection",
    defaultMessage: "No leads lassoed",
    description: "No leads lassoed",
  },
  selectLeads: {
    id: "map.tool.lasso.leadAnnotation.selectLeads",
    defaultMessage: "Select {count, plural, one {lead} other {leads}}",
    description: "Select leads button title",
  },
  undo: {
    id: "map.tool.lasso.leadAnnotation.undo",
    defaultMessage: "Undo",
    description: "Undo button title",
  },
});

const LassoAnnotation: React.FC<Props> = ({
  cleanLassoPaths,
  leads,
  paths,
  pathsCount,
  selectMapTool,
  selectMultiplyItems,
  setHighlight,
}) => {
  const analytics = useExtendedAnalytics("Lasso Tool", useAnalytics());
  const intl = useIntl();
  const { map } = useMap();
  const { viewState } = useMapView();

  const isVisible = viewState.tool === MapTool.LASSO && pathsCount && leads.length;

  const [area, bounds] = useMemo(() => {
    if (isVisible) {
      const area = new google.maps.Polygon({ paths });
      const bounds = new google.maps.LatLngBounds();
      area
        .getPath()
        .forEach((item) => bounds.extend(new google.maps.LatLng(item.lat(), item.lng())));
      return [area, bounds];
    }
    return [];
  }, [isVisible, paths]);

  const matches = useMemo(() => {
    if (isVisible && area) {
      return leads.filter(
        (lead) =>
          lead.geometry?.location &&
          google.maps.geometry.poly.containsLocation(lead.geometry.location, area)
      );
    }
  }, [area, isVisible, leads]);

  useEffect(() => {
    const highlightedItems = new Set(matches?.map((lead) => leadIdGetter(lead)));
    setHighlight(highlightedItems);
  }, [matches, setHighlight]);

  const position: google.maps.LatLngLiteral = {
    lat: bounds?.getCenter().lat() ?? 0,
    lng: bounds?.getNorthEast().lng() ?? 0,
  };

  const handleSelectClick = useCallback(() => {
    if (matches) {
      selectMultiplyItems(matches.map(leadIdGetter));
      analytics.completed(["Select leads"]);
    }

    setHighlight(new Set());
    cleanLassoPaths();
    selectMapTool(undefined);
  }, [analytics, cleanLassoPaths, matches, selectMapTool, selectMultiplyItems, setHighlight]);

  const handleUndoClick = useCallback(() => {
    setHighlight(new Set());
    cleanLassoPaths();
    analytics.cancelled(["Undo"]);
  }, [analytics, cleanLassoPaths, setHighlight]);

  const count = matches?.length ?? 0;

  useEffect(() => {
    if (!isVisible) {
      cleanLassoPaths();
    }
  }, [isVisible, cleanLassoPaths]);

  return map && position && isVisible ? (
    <OverlayView map={map} position={position}>
      <div className={styles.container}>
        <div className={styles.arrow} />
        <Interactive>
          <div className={styles.header}>
            <span className={styles.count}>
              {count
                ? intl.formatMessage(messages.count, { count })
                : intl.formatMessage(messages.emptySelection)}
            </span>
            <Button className={styles.undoBtn} onClick={handleUndoClick} type="text">
              {intl.formatMessage(messages.undo)}
            </Button>
          </div>

          <div className={styles.body}>
            <Button block className={styles.btn} disabled={!count} onClick={handleSelectClick}>
              {intl.formatMessage(messages.selectLeads, { count })}
            </Button>
          </div>
        </Interactive>
      </div>
    </OverlayView>
  ) : null;
};

const mapStateToProps = (state: RootState) => ({
  paths: getMapLassoPaths(state),
  pathsCount: getMapLassoPathsCount(state),
});

const mapDispatchToProps = {
  cleanLassoPaths,
  selectMapTool,
  selectMultiplyItems,
  setHighlight,
};
export default connect(mapStateToProps, mapDispatchToProps)(LassoAnnotation);
